import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Trash2 } from "lucide-react";
import { api, API } from "../../lib/api";
import { QrShareCard } from "./QrShareCard";

export const PhotosPanel = () => {
  const [photos, setPhotos] = useState([]);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = () => api.get("/admin/photos").then((r) => setPhotos(r.data.photos)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const upload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post("/admin/photos", fd);
      }
      toast.success(files.length === 1 ? "Archivo subido" : `${files.length} archivos subidos`);
      setFiles([]);
      if (inputRef.current) inputRef.current.value = "";
      load();
    } catch {
      toast.error("Error al subir las fotos");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm("¿Eliminar esta foto?")) return;
    await api.delete(`/admin/photos/${p.id}`).catch(() => {});
    toast.success("Foto eliminada");
    load();
  };

  return (
    <div data-testid="photos-panel">
      <QrShareCard />
      <div className="bg-cream border border-clay/15 rounded-3xl p-8 mt-6">
        <p className="text-[11px] uppercase tracking-[0.3em] text-stone">Subir fotos y videos del gran día</p>
        <p className="mt-2 text-sm text-stone">Los invitados los verán en la sección “Fotos de la Boda” con su código.</p>
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <input
            data-testid="photo-upload-input"
            ref={inputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={(e) => setFiles(Array.from(e.target.files || []))}
            className="flex-1 text-sm text-stone file:mr-4 file:rounded-full file:border-0 file:bg-peach/40 file:text-clay file:px-6 file:py-3 file:text-[11px] file:uppercase file:tracking-[0.2em] hover:file:bg-peach/60"
          />
          <button
            data-testid="photo-upload-submit"
            onClick={upload}
            disabled={uploading || files.length === 0}
            className="rounded-full bg-clay text-cream px-8 py-4 text-[11px] uppercase tracking-[0.25em] hover:bg-clay-deep transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            <ImagePlus size={16} />
            {uploading ? "Subiendo…" : files.length > 0 ? `Subir ${files.length} archivo${files.length > 1 ? "s" : ""}` : "Subir archivos"}
          </button>
        </div>
      </div>

      {photos.length === 0 ? (
        <p data-testid="photos-empty" className="mt-10 text-center text-stone font-serif italic">
          Aún no has subido fotos.
        </p>
      ) : (
        <div data-testid="admin-photos-grid" className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          {photos.map((p) => (
            <div key={p.id} className="relative group overflow-hidden rounded-2xl border border-clay/10">
              {p.content_type?.startsWith("video/") ? (
                <video src={`${API}/photos/file/${p.id}`} className="w-full aspect-square object-cover" muted playsInline preload="metadata" controls />
              ) : (
                <img src={`${API}/photos/file/${p.id}`} alt={p.original_filename || "Foto"} className="w-full aspect-square object-cover" loading="lazy" />
              )}
              <span className="absolute bottom-2 left-2 bg-cream/90 text-ink text-[10px] uppercase tracking-[0.15em] rounded-full px-3 py-1">
                {p.uploaded_by ? `por ${p.uploaded_by}` : "Novios"}
              </span>
              <button
                data-testid={`delete-photo-${p.id}`}
                onClick={() => remove(p)}
                className="absolute top-2 right-2 bg-cream/90 text-clay-deep rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
